import { useDebounceFn } from 'ahooks';
import { Input, InputProps } from 'antd';
import { trim } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { QUANTITY_DEBOUNCE_MILLISECOND } from 'utils/constants';
import { isFloat } from 'utils/misc';

type ValueType = InputProps['value'];


interface Props extends Omit<InputProps, 'onChange' | 'onBlur'> {
  isInteger?: boolean;
  min?: number;
  max?: number;
  onChange?: (val: number) => void;
  onBlur?: (val: number) => void;
  value?: number;
  onEmpty?: () => void;
  allowEmpty?: boolean;
  precision?: number;
  allowLeadingDecimal?: boolean;
  triggerOnBlur?: boolean;
}


export const RevxSvcInputNumber = ({
  isInteger,
  min,
  max,
  precision,
  allowLeadingDecimal,
  triggerOnBlur,
  ...props
}: Props) => {
  const [realVal, setRealVal] = useState<ValueType>(props.value);
  const [displayVal, setDisplayVal] = useState<ValueType>(props.value);
  const displayValRef = useRef<ValueType>(props.value);
  const valuePattern = isInteger ? /^-?\d+$/ : /^-?\d+\.?\d*$/;

  useEffect(() => {
    displayValRef.current = displayVal;
  }, [displayVal]);

  const { run: updateMarkup, cancel: cancelUpdateMarkup } = useDebounceFn(
    async (payload?: number) => {
      if (payload == null || isNaN(payload)) {
        setRealVal('');
        setDisplayVal('');
        return;
      }
      setRealVal(Number(payload)?.toFixed(precision || 2));
      setDisplayVal(Number(payload)?.toFixed(precision || 2));
    },
    { wait: QUANTITY_DEBOUNCE_MILLISECOND }
  );

  useEffect(() => {
    if (props.value === undefined) {
      return;
    }

    const shouldKeepIntermediateDecimal =
      allowLeadingDecimal &&
      !isInteger &&
      typeof displayValRef.current === 'string' &&
      displayValRef.current.endsWith('.') &&
      Number(displayValRef.current) === props.value;

    if (shouldKeepIntermediateDecimal) {
      return;
    }

    setRealVal(props.value);
    setDisplayVal(props.value);
    if (props.suffix === '$' || props.prefix === '$') {
      cancelUpdateMarkup();
      updateMarkup(props.value);
    }
  }, [
    allowLeadingDecimal,
    isInteger,
    props.value,
    props.prefix,
    props.suffix,
    cancelUpdateMarkup,
    updateMarkup,
  ]);

  return (
    <Input
      {...props}
      value={displayVal}
      onChange={(e) => {
        // Same reason as the measurement fields: "3,5" matched neither the float check nor the
        // value pattern, so it fell through every branch and left the field empty.
        let val = trim(e.target.value).replace(',', '.');
        if (val === '-') {
          if (!min || min < 0) {
            // allow negative number
            setDisplayVal(val);
            return;
          }
        }

        if (allowLeadingDecimal && !isInteger && val === '.') {
          val = `0${val}`;
        }
        console.log('RevxSvcInputNumber normalized value:', val);
        if (!isFloat(val, precision)) {
          console.log('RevxSvcInputNumber invalid float input:', val);
          return;
        }
        if (valuePattern.test(val)) {
          const numberVal =
            val.includes('.') &&
            ['.', '0'].includes(val.substr(val.length - 1, 1)) &&
            !isInteger
              ? val
              : +val;
          const comparableVal = Number(numberVal);
          if (
            (min === undefined || comparableVal >= min) &&
            (max === undefined || comparableVal <= max)
          ) {
            console.log(
              'RevxSvcInputNumber valid input, update value:',
              numberVal
            );
            setDisplayVal(numberVal);
            setRealVal(numberVal);
            if (props.onChange && !triggerOnBlur) {
              props.onChange(Number(numberVal));
            }
          }
        }

        if (!val) {
          // allow to input empty string so that the user can edit the number using a free style
          setDisplayVal(val);
          if (props.allowEmpty) {
            if (props.onChange) {
              props.onChange(null as any);
            }
            if (props.onEmpty) {
              props.onEmpty();
            }
          }
        }
      }}
      onBlur={(e) => {
        console.log(
          'RevxSvcInputNumber onBlur display value:',
          displayValRef.current
        );
        const value =
          precision && realVal
            ? Number(realVal || 0)?.toFixed(precision)
            : Number(realVal || 0);
        setDisplayVal(value);
        console.log('RevxSvcInputNumber onBlur normalized value:', value);
        if (props.onChange && triggerOnBlur) {
          props.onChange(Number(value || 0));
        }

        if (props.onBlur) {
          props.onBlur(Number(value || 0));
        }
      }}
    />
  );
};
